import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { fetchAds } from '../services/s3-service';
import { EventData, establishSSEConnection } from '../helpers/sse';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isBetween from 'dayjs/plugin/isBetween';
import SplitScreen from '../components/SplitScreen';
import NightModeOverlay from '../components/NightModeOverlay';
import { Ad } from '../models/models';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

interface DimmingSettings {
    mode: string;
    startHour: number;
    endHour: number;
}

const DEFAULT_DIMMING: DimmingSettings = { mode: 'off', startHour: 22, endHour: 6 };
const IMAGE_DISPLAY_MS = 7500;

const isVideo = (url: string) => /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(url);

const shuffleArray = (array: any[]) => {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
};

const filterActiveAds = (allAds: Ad[]): Ad[] => {
    const now = dayjs().utc();
    return allAds.filter(ad => {
        if (ad.startDate && ad.endDate) {
            return now.isBetween(dayjs(ad.startDate).utc(), dayjs(ad.endDate).utc(), null, '[]');
        }
        return true;
    });
};

const Slide = React.memo(({ url, isSelected, onVideoEnd }: { url: string; isSelected?: boolean; onVideoEnd?: () => void }) => {
    if (isVideo(url)) {
        return (
            <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ReactPlayer
                    url={url}
                    playing={isSelected !== undefined ? isSelected : true}
                    controls={false}
                    muted={true}
                    width="100%"
                    height="100%"
                    style={{ objectFit: 'contain' }}
                    onEnded={onVideoEnd}
                />
            </div>
        );
    }

    return (
        <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img
                alt="Media"
                src={url}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
        </div>
    );
});

const Play: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [currentSlide, setCurrentSlide] = useState(0);
    const [filteredAds, setFilteredAds] = useState<Ad[]>([]);
    const [splitScreenCount, setSplitScreenCount] = useState(0);
    const [dimmingSettings, setDimmingSettings] = useState<DimmingSettings>(DEFAULT_DIMMING);
    const imageTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Always-current ref — read by timer callbacks and handleVideoEnd so they never
    // operate on a stale snapshot, even if filteredAds changed while the timer was running
    const filteredAdsRef = useRef<Ad[]>([]);
    filteredAdsRef.current = filteredAds; // sync during render, no effect needed

    // Derive params from location — primitives, stable across renders
    const stateParams = location.state as { companyId?: string; tvId?: number } | null;
    const searchParams = new URLSearchParams(location.search);
    const companyId = stateParams?.companyId ?? searchParams.get('companyId') ?? '';
    const tvId = stateParams?.tvId ?? Number(searchParams.get('tvId') ?? 0);

    // Redirect if params are missing — in an effect, never during render
    useEffect(() => {
        if (!companyId || !tvId) navigate('/');
    }, [companyId, tvId, navigate]);

    // Reset currentSlide if it falls outside the current ad list (e.g. ads removed while on slide N)
    useEffect(() => {
        if (filteredAds.length > 0 && currentSlide >= filteredAds.length) {
            setCurrentSlide(0);
        }
    }, [filteredAds.length, currentSlide]);

    // resetSlide: true  → reshuffle and restart from slide 0 (content changed)
    // resetSlide: false → refresh settings/filters without disrupting the current slide
    const fetchFiles = useCallback(async (resetSlide: boolean) => {
        if (!companyId || !tvId) return;
        const { ads: fetchedAds, splitScreenOption, dimmingSettings: newDimming } = await fetchAds(+companyId, +tvId);

        setSplitScreenCount(splitScreenOption);
        setDimmingSettings(newDimming ?? DEFAULT_DIMMING);

        if (resetSlide) {
            shuffleArray(fetchedAds);
            setCurrentSlide(0);
        }
        setFilteredAds(filterActiveAds(fetchedAds));
    }, [companyId, tvId]);

    const handleNewEvent = useCallback((eventData: EventData) => {
        console.log('Received SSE event:', eventData.action);

        if (eventData.action === 'HDMI_CONTROL' && eventData.command) {
            console.log('Received HDMI command:', eventData.command);
        } else if (eventData.action === 'UploadMedia' || eventData.action === 'RemoveMedia') {
            // Ad content changed — reshuffle and restart from the beginning
            fetchFiles(true);
        } else if (eventData.action === 'UpdateSplitScreen' || eventData.action === 'UpdateDimming') {
            // Settings only — keep the current slide playing
            fetchFiles(false);
        }
    }, [fetchFiles]);

    useEffect(() => {
        if (!companyId || !tvId) return;

        const requestFullScreen = () => {
            if (document.documentElement.requestFullscreen) {
                document.documentElement.requestFullscreen().catch(err => {
                    console.error(`Fullscreen error: ${err.message}`);
                });
            }
        };

        requestFullScreen();
        fetchFiles(true);

        const closeSSE = establishSSEConnection(companyId.toString(), tvId, handleNewEvent);
        return () => { if (closeSSE) closeSSE(); };
    }, [companyId, tvId, fetchFiles, handleNewEvent]);

    // Uses filteredAdsRef so it always reads the current list length,
    // even if filteredAds changed while the video was playing
    const handleVideoEnd = useCallback(() => {
        const len = filteredAdsRef.current.length;
        if (len > 0) setCurrentSlide(prev => (prev + 1) % len);
    }, []); // no deps — reads from ref, not closure

    // Image display timer — restarts when the current slide or ad list changes
    useEffect(() => {
        if (imageTimeoutRef.current) {
            clearTimeout(imageTimeoutRef.current);
            imageTimeoutRef.current = null;
        }

        const ads = filteredAdsRef.current;
        // Guard against out-of-bounds: if currentSlide is beyond the new list, the
        // bounds-check effect above will correct it on the next render — skip the timer
        if (ads.length > 0 && currentSlide < ads.length && !isVideo(ads[currentSlide].mediaLink)) {
            imageTimeoutRef.current = setTimeout(() => {
                // Read from ref at fire time so the modulo uses the live list length
                const len = filteredAdsRef.current.length;
                if (len > 0) setCurrentSlide(prev => (prev + 1) % len);
            }, IMAGE_DISPLAY_MS);
        }

        return () => {
            if (imageTimeoutRef.current) {
                clearTimeout(imageTimeoutRef.current);
                imageTimeoutRef.current = null;
            }
        };
    }, [currentSlide, filteredAds]); // filteredAds in deps: re-run if content type at currentSlide changes

    const customRenderItem = useCallback((item: React.ReactNode, options?: { isSelected: boolean }) => {
        if (!React.isValidElement(item)) return null;
        const { type, props } = item;
        if (type === Slide) {
            return React.createElement(type, { ...props, ...options, onVideoEnd: handleVideoEnd });
        }
        return React.createElement(type, props);
    }, [handleVideoEnd]);

    return (
        <NightModeOverlay dimmingSettings={dimmingSettings}>
            {filteredAds.length > 0 &&
                (splitScreenCount === 0 ? (
                    <Carousel
                        selectedItem={currentSlide}
                        renderItem={customRenderItem}
                        showStatus={false}
                        showIndicators={false}
                        showArrows={false}
                        showThumbs={false}
                    >
                        {filteredAds.map((ad, index) => (
                            isVideo(ad.mediaLink) ? (
                                <Slide
                                    key={`video-${index}`}
                                    url={ad.mediaLink}
                                    onVideoEnd={handleVideoEnd}
                                />
                            ) : (
                                <div
                                    key={`image-${index}`}
                                    className="carousel-image"
                                    style={{
                                        height: '100vh',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'black',
                                    }}
                                >
                                    <img
                                        alt={`Media ${index}`}
                                        src={ad.mediaLink}
                                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                    />
                                </div>
                            )
                        ))}
                    </Carousel>
                ) : (
                    <SplitScreen ads={filteredAds} splitCount={splitScreenCount} />
                ))
            }
        </NightModeOverlay>
    );
};

export default Play;
